import React, { useState } from 'react';
import { Advisor } from '../types';
import UserData from './UserData/UserData';

interface AdvisorModalProps {
  advisor: Advisor;
  isOpen: boolean;
  onClose: () => void;
}

const AdvisorModal: React.FC<AdvisorModalProps> = ({ advisor, isOpen, onClose }) => {
  const[isRegistered,setIsRegistered] = useState(false);
  const[pendingContactType,setPendingContactType] = useState<'whatsapp' | 'email' | null>(null);
  if (!isOpen) return null;

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="text-yellow-400">★</span>);
    }

    if (hasHalfStar) {
      stars.push(<span key="half" className="text-yellow-400">☆</span>);
    }

    const remainingStars = 5 - stars.length;
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<span key={`empty-${i}`} className="text-gray-300">★</span>);
    }

    return stars;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  //Added Google Analytics event tracking for contact clicks
  const handleContactClick = (type: 'whatsapp' | 'email') => {
    if (window.gtag) {
      window.gtag('event', 'contact_click', {
        event_category: 'User Interaction',
        event_label: type === 'whatsapp' ? 'WhatsApp Contact' : 'Email Contact',
        value: advisor.name, 
      });
    }

    if(!isRegistered){
      setPendingContactType(type);
      return;
    }
    if(type === 'whatsapp'){
      window.open(`https://wa.me/${advisor.phone}?text=Hi ${advisor.name}, I found your profile on Misu Capital and would like to get in touch with you regarding my financial advisory needs. Looking forward to your response!`, '_blank');
    }
    else{
      window.open(`mailto:${advisor.email}?subject=Financial Advisory Consultation`, '_self');
    }
  };
  const handleUserDataSubmit = ()=>{
    setIsRegistered(true);
    
    if (window.gtag) {
      window.gtag('event', 'user_registered_before_contact', {
        event_category: 'User Interaction',
        event_label: pendingContactType || 'unknown',
      });
    }

    if(pendingContactType){
      handleContactClick(pendingContactType);
      setPendingContactType(null);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 transition-opacity duration-300"
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto transform transition-all duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-4 sm:p-6 flex justify-between items-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Advisor Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Header Info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start sm:space-x-6 mb-6">
            
            {/* we will add this in future for image  */}
            
            {/* <img
              src={advisor.image}
              alt={advisor.name}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mb-4 sm:mb-0"
            /> */}
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2">
              <h3 className="text-2xl font-bold text-gray-900">{advisor.name}</h3>
              {advisor.featured && (
                <span className="bg-yellow-200 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  ⭐ Featured
                </span>
              )}
            </div>
              <p className="text-lg text-gray-600 mb-2">{advisor.title}</p>
              {/* advisor website  */}
              {advisor.website && (
            <a
              href={advisor.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-1 text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded hover:underline"
            >
              🌐 {advisor.website.replace(/^https?:\/\//, '')}
            </a>
          )}
              {/* we will add this rating section in future */}

              {/* <div className="flex justify-center sm:justify-start items-center space-x-2 mb-2">
                <div className="flex">{renderStars(advisor.rating)}</div>
                <span className="text-sm text-gray-600">
                  {advisor.rating} ({advisor.reviewCount} reviews)
                </span>
              </div> */}
              
              {/* we will add this pricing in future */}

              {/* <p className="text-xl font-semibold text-primary-600">
                ${advisor.hourlyRate}/hour
              </p> */}
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Experience</h4>
              <p className="text-gray-600">{advisor.experience} years</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Location</h4>
              <p className="text-gray-600">📍 {advisor.location}</p>
            </div>
          </div>

          {/* About */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">About</h4>
            <p className="text-gray-600 leading-relaxed">{advisor.bio}</p>
          </div>

          {/* Specializations */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">How Can I Help</h4>
            <div className="flex flex-wrap gap-2">
              {advisor.specializations.map((spec, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                >
                  {spec}
                </span>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">Certifications I Hold</h4>
            <div className="flex flex-wrap gap-2">
              {advisor.certifications.map((cert, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm"
                >
                  {cert}
                </span>
              ))}
            </div>
          </div>

          {/* Languages */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">Languages I Speak</h4>
            <div className="flex flex-wrap gap-2">
              {advisor.languages.map((lang, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
          {/* if registeration required */}
          {pendingContactType && !isRegistered && (
              <UserData
                onSubmit={handleUserDataSubmit}
                onClose={() => setPendingContactType(null)}
            />
          )}

          {/* Contact */}
        <div className="border-t border-gray-200 pt-6">
        <h4 className="font-semibold text-gray-900 mb-4">Contact {advisor.name}</h4>
        <div className="flex flex-col sm:flex-row gap-4">
      {advisor.phone ? (
      <>
        <button
          onClick={() => handleContactClick('whatsapp')}
          className="flex items-center justify-center gap-3 w-full px-5 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#1EBE5D] transition font-medium text-base"
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
            alt="WhatsApp Logo"
            className="w-5 h-5"
          />
          Chat on WhatsApp
        </button>
        {advisor.email && (
          <button
            onClick={() => handleContactClick('email')}
            className="btn-secondary flex items-center justify-center space-x-2 w-full"
          >
            <span>✉️</span>
            <span>Send Email</span>
          </button>
        )}
      </>
    ) : (
      advisor.email && (
        <button
          onClick={() => handleContactClick('email')}
          className="btn-secondary flex items-center justify-center space-x-2 w-full"
        >
          <span>✉️</span>
          <span>Send Email</span>
        </button>
      )
    )}
    </div>
    </div>
    </div>
    </div>
    </div>
  );
};

export default AdvisorModal;
